import { BASE_URL } from "../Constants";
import api from "../components/login/interceptors";

// Organisations and Roles (spec v0.1). Every call acts on the active organisation in the token.
const ORG_URL = `${BASE_URL}/api/organisations`;
const CURRENT_URL = `${ORG_URL}/current`;
const ADMIN_URL = `${BASE_URL}/api/admin/organisations`;

export const ORG_ROLE = {
  OWNER: "owner",
  ADMIN: "admin",
  HR: "hr",
  PANELIST: "panelist",
};

export const ORG_ROLE_LABEL = {
  owner: "Owner",
  admin: "Admin",
  hr: "HR",
  panelist: "Panelist",
  super_admin: "Support",
};

export const ORG_ROLE_DESCRIPTION = {
  owner: "Everything an Admin can do, plus the plan, the resume limit, managing Admins and deleting the organisation.",
  admin: "Runs the team and the settings. Cannot change the Owner or other Admins.",
  hr: "Does all the hiring work, sees every job and makes hire or reject decisions.",
  panelist: "Logs in to interview. Sees only assigned interviews and their own feedback.",
};

export const MEMBER_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  EXPIRED: "expired",
  REQUESTED: "requested",
};

export const MEMBER_TYPE = {
  MEMBER: "member",
  INVITE: "invite",
  JOIN_REQUEST: "join_request",
};

export const ORG_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended",
  DELETED: "deleted",
};

export const roleLabel = (role) => ORG_ROLE_LABEL[role] || "—";

// The organisation a person belongs to (as a member, not through a support session): the one they own,
// otherwise their first. A Super Admin's own plan and billing are for this one, whichever they have opened.
export const homeOrganisation = (session) => {
  const mine = (session?.organisations || []).filter((org) => org?.id && org.role && org.role !== "super_admin");
  return mine.find((org) => org.role === ORG_ROLE.OWNER) || mine[0] || null;
};

// Section 3.2 permission matrix. Panelist "assigned only" rows are enforced by the backend.
const PERMISSIONS = {
  viewOrganisation: ["owner", "admin", "hr", "panelist"],
  deleteOrganisation: ["owner"],
  transferOwnership: ["owner"],
  manageQuota: ["owner"],
  manageAdmins: ["owner"],
  manageSettings: ["owner", "admin"],
  manageBilling: ["owner", "admin"],
  approveJoinRequests: ["owner", "admin"],
  viewAuditLog: ["owner", "admin"],
  manageHr: ["owner", "admin"],
  managePanelists: ["owner", "admin", "hr"],
  viewTeam: ["owner", "admin"],
  hiring: ["owner", "admin", "hr"],
};

// Tokens issued before organisations launch carry no org_role, so nothing is restricted.
// A Super Admin inside an organisation uses the Owner gates, including when their membership there is panelist.
// The backend still checks permissions.
const roleForPermissions = (session) => {
  const insideOrganisation = Boolean(session.role);
  if (session.role === "super_admin" || (session.isSuperAdmin && insideOrganisation)) return "owner";
  return session.role;
};

export const can = (session, permission) => {
  if (!session?.orgsEnabled) return permission === "hiring";
  return (PERMISSIONS[permission] || []).includes(roleForPermissions(session));
};

// Roles this member may grant to others when inviting, approving or changing a role
export const assignableRoles = (session) => {
  const roles = [];
  if (can(session, "manageAdmins")) roles.push(ORG_ROLE.ADMIN);
  if (can(session, "manageHr")) roles.push(ORG_ROLE.HR);
  if (can(session, "managePanelists")) roles.push(ORG_ROLE.PANELIST);
  return roles;
};

// Whether this member may change or remove someone who currently holds targetRole
export const canManageMember = (session, targetRole) => {
  if (targetRole === ORG_ROLE.OWNER) return false;
  if (targetRole === ORG_ROLE.ADMIN) return can(session, "manageAdmins");
  if (targetRole === ORG_ROLE.HR) return can(session, "manageHr");
  if (targetRole === ORG_ROLE.PANELIST) return can(session, "managePanelists");
  return false;
};

const decodeTokenPayload = (token) => {
  try {
    const part = (token || "").split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

// Claims from section 8.2, used until GET /me has been stored
const sessionFromToken = () => {
  const claims = decodeTokenPayload(localStorage.getItem("jwtToken")) || {};
  const role = claims.org_role ? String(claims.org_role).toLowerCase() : "";
  return {
    orgsEnabled: Boolean(role) || Boolean(claims.is_super_admin),
    organisationId: claims.user_id || "",
    actorId: claims.actor_id || claims.user_id || "",
    role,
    isSuperAdmin: Boolean(claims.is_super_admin),
    isSupportSession: Boolean(claims.is_super_admin && claims.support_session),
    permissions: [],
    organisations: null,
  };
};

const ME_KEY = "me_profile";

// Same organisation object GET /me and PATCH /organisation return.
const organisationFromMe = (org = {}) => ({
  id: org.org_id || org.id || "",
  name: org.name || "",
  domain: org.domain || "",
  status: org.status || "",
  resumeUsage: org.resume_usage ?? 0,
  resumeLimit: org.resume_limit ?? 100,
  ownerUserId: org.owner_user_id || "",
  createdAt: org.created_at || "",
});

const normalizeMe = (data = {}) => {
  const user = data.user || {};
  const org = data.organisation || null;
  return {
    user: {
      userId: user.user_id || "",
      email: user.email || "",
      firstName: user.first_name || "",
      lastName: user.last_name || "",
      isSuperAdmin: Boolean(user.is_super_admin),
    },
    organisation: org ? organisationFromMe(org) : null,
    role: data.role ? String(data.role).toLowerCase() : "",
    permissions: Array.isArray(data.permissions) ? data.permissions : [],
    organisations: (data.organisations || []).map((item) => ({
      id: item.org_id || "",
      name: item.name || "",
      role: item.role ? String(item.role).toLowerCase() : "",
    })),
    // The panelist record linked to this person in the active organisation, or null
    panelist: data.panelist || null,
  };
};

export const cachedMe = () => {
  try {
    return JSON.parse(localStorage.getItem(ME_KEY)) || null;
  } catch {
    return null;
  }
};

const sessionFromMe = (me) => {
  const support = me.role === "super_admin";
  const inOrganisation = Boolean(me.organisation);
  return {
    orgsEnabled: inOrganisation || me.user.isSuperAdmin,
    organisationId: me.organisation?.id || "",
    actorId: me.user.userId,
    role: me.role,
    isSuperAdmin: me.user.isSuperAdmin,
    // Super Admin inside an organisation they are not a member of
    isSupportSession: support,
    permissions: me.permissions,
    organisations: me.organisations,
  };
};

// GET /me when it has been loaded; otherwise the token
export const readSession = () => {
  const me = cachedMe();
  return me ? sessionFromMe(me) : sessionFromToken();
};

// Stores the signed-in profile and the switcher list. organisation null means no active organisation.
export const applyMe = (data) => {
  const me = normalizeMe(data);
  try {
    localStorage.setItem(ME_KEY, JSON.stringify(me));
  } catch {
    // The next read falls back to the token
  }
  const { user, organisation } = me;
  localStorage.setItem(
    "user_data",
    JSON.stringify({
      user_id: user.userId,
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
    })
  );
  if (!organisation && !user.isSuperAdmin) {
    localStorage.setItem(NO_ORGANISATION_FLAG, "1");
    localStorage.removeItem("active_organisation");
    return me;
  }
  localStorage.removeItem(NO_ORGANISATION_FLAG);
  if (organisation) cacheActiveOrganisation({ id: organisation.id, name: organisation.name });
  else localStorage.removeItem("active_organisation");
  return me;
};

export const fetchMe = async () => {
  const response = await api.get(`${BASE_URL}/api/me`);
  return applyMe(response.data || {});
};

// Fired after the signed-in person's name changes, so the sidebar can show it without a reload
export const PROFILE_UPDATED_EVENT = "sifthire:profile-updated";

// PATCH /api/me saves the whole Profile form in one call. Email is never sent; the server rejects it.
// panelist holds designation, skills and experience, sent only when GET /me returned a panelist profile.
export const buildProfilePayload = ({ firstName, lastName, panelist } = {}) => ({
  first_name: String(firstName || "").trim(),
  last_name: String(lastName || "").trim(),
  ...(panelist || {}),
});

export const updateMyProfile = async (fields) => {
  const response = await api.patch(`${BASE_URL}/api/me`, buildProfilePayload(fields));
  const me = response.data?.user ? applyMe(response.data) : await fetchMe();
  window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
  return me;
};

// The Super Admin console lives in Settings
export const SUPER_ADMIN_CONSOLE_PATH = "/settings?tab=all-organisations";

// Where a session starts: panelists only have interviews; a Super Admin outside any organisation gets the console
export const homePathFor = (session) => {
  if (session.isSuperAdmin && !session.role) return SUPER_ADMIN_CONSOLE_PATH;
  if (session.orgsEnabled && !can(session, "hiring")) return "/interviews";
  return "/jobs";
};

// Login said this account is in no active organisation. A missing flag is the pre-organisations model.
const NO_ORGANISATION_FLAG = "no_organisation";
export const NO_ORGANISATION_PATH = "/no-organisation";

export const isWithoutOrganisation = () => localStorage.getItem(NO_ORGANISATION_FLAG) === "1";

const present = (value) => (value == null || value === "" ? null : value);

// org_id, org_role and organisation_name are null when the account has no active organisation.
// A Super Admin with no organisation still has the console.
export const applyLoginOrganisation = ({ org_id, org_role, organisation_name, is_super_admin } = {}) => {
  const orgId = present(org_id);
  const orgRole = present(org_role);
  const organisationName = present(organisation_name);
  const inOrganisation = Boolean(orgId || orgRole || organisationName);
  if (!inOrganisation && !is_super_admin) {
    localStorage.setItem(NO_ORGANISATION_FLAG, "1");
    localStorage.removeItem("active_organisation");
    return;
  }
  localStorage.removeItem(NO_ORGANISATION_FLAG);
  if (inOrganisation) cacheActiveOrganisation({ id: orgId || "", name: organisationName || "" });
  else localStorage.removeItem("active_organisation");
};

// After the access token is stored. No-organisation accounts do not fall through to Jobs.
export const landingPathAfterLogin = () => {
  if (isWithoutOrganisation()) return NO_ORGANISATION_PATH;
  return homePathFor(readSession());
};

// A new token means a new organisation: load /me for that token, then reload so nothing from the old one stays on screen
export const startSession = (accessToken, path = "/") => {
  localStorage.setItem("jwtToken", accessToken);
  localStorage.removeItem("active_organisation");
  localStorage.removeItem(ME_KEY);
  localStorage.removeItem(NO_ORGANISATION_FLAG);
  fetchMe()
    .catch(() => {})
    .finally(() => window.location.assign(path));
};

export const cacheActiveOrganisation = (organisation) => {
  try {
    localStorage.setItem("active_organisation", JSON.stringify(organisation));
  } catch {
    // Display cache only
  }
};

export const cachedActiveOrganisation = () => {
  try {
    return JSON.parse(localStorage.getItem("active_organisation")) || null;
  } catch {
    return null;
  }
};

const normalizeOrganisation = (org = {}) => ({
  id: org.org_id || org.organisation_id || org.id || "",
  name: org.name || "",
  domain: org.domain || "",
  role: org.role ? String(org.role).toLowerCase() : "",
  status: org.status || ORG_STATUS.ACTIVE,
  ownerUserId: org.owner?.user_id || org.owner_user_id || "",
  ownerName: org.owner?.name || org.owner_name || "",
  ownerEmail: org.owner?.email || org.owner_email || "",
  memberCount: org.member_count ?? null,
  resumeLimit: org.resume_limit ?? 100,
  resumeUsage: org.resume_usage ?? 0,
  createdAt: org.created_at || "",
});

const personName = (row = {}) => [row.first_name, row.last_name].filter(Boolean).join(" ") || row.name || "";

// One row from GET /organisation/members: member, invite, or join_request
const normalizeMemberRow = (row = {}) => ({
  type: row.type || MEMBER_TYPE.MEMBER,
  userId: row.user_id || "",
  inviteId: row.invite_id || "",
  name: personName(row),
  email: row.email || "",
  role: row.role ? String(row.role).toLowerCase() : "",
  status: row.status ? String(row.status).toLowerCase() : "",
  message: row.message || "",
  invitedBy: row.invited_by || "",
  approvedBy: row.approved_by || "",
  joinedAt: row.joined_at || "",
  createdAt: row.created_at || "",
  expiresAt: row.expires_at || "",
});

const normalizeAuditEntry = (entry = {}) => ({
  id: entry.entry_id || entry.id || "",
  actorName: entry.actor_name || entry.actor_email || "",
  actorEmail: entry.actor_email || "",
  isSuperAdmin: Boolean(entry.is_super_admin),
  action: entry.action || "",
  summary: entry.summary || entry.description || "",
  createdAt: entry.created_at || "",
});

export const formatOrgDate = (value, withTime = false) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

// Organisations the signed-in person belongs to (for the switcher)
export const fetchMyOrganisations = async () => {
  const response = await api.get(`${ORG_URL}/mine`);
  return {
    organisations: (response.data?.organisations || []).map(normalizeOrganisation),
    activeId: response.data?.active_organisation_id || "",
  };
};

// Leaving or deleting the active organisation ends this token's access to it, so GET /me fails afterwards.
// Call this first, while the token still works. Returns another active organisation to move to, or null.
export const findNextOrganisation = async (currentId) => {
  let list = cachedMe()?.organisations || [];
  try {
    list = (await fetchMyOrganisations()).organisations;
  } catch {
    // Fall back to the list from the last GET /me
  }
  return (
    list.find((org) => org.id && org.id !== currentId && (!org.status || org.status === ORG_STATUS.ACTIVE)) || null
  );
};

// Member of the target organisation. The returned token replaces the stored one; the previous token is ended.
export const switchOrganisation = async (organisationId) => {
  const response = await api.post(`${BASE_URL}/api/session/switch`, { org_id: organisationId });
  return response.data?.access_token;
};

// Owner or Admin. The response organisation matches GET /me.
export const updateOrganisation = async ({ name }) => {
  const response = await api.patch(ORGANISATION_URL, { name });
  const organisation = organisationFromMe(response.data?.organisation || {});
  const me = cachedMe();
  if (me) {
    const next = {
      ...me,
      organisation: { ...(me.organisation || {}), ...organisation },
      organisations: (me.organisations || []).map((item) =>
        item.id === organisation.id ? { ...item, name: organisation.name || item.name } : item
      ),
    };
    try {
      localStorage.setItem(ME_KEY, JSON.stringify(next));
    } catch {
      // The next GET /me refreshes the name
    }
  }
  if (organisation.id || organisation.name) cacheActiveOrganisation({ id: organisation.id, name: organisation.name });
  return organisation;
};

// Owner. The caller confirms first. Everyone in this organisation loses access.
export const deleteOrganisation = async () => {
  const response = await api.delete(ORGANISATION_URL);
  return response.data || {};
};

// The Owner sends role "owner". They become Admin. Confirm before calling this.
export const transferOwnership = (userId) => changeMemberRole(userId, ORG_ROLE.OWNER);

// Owner. message is optional. Replaces the old limit email.
export const requestMoreQuota = async (message) => {
  const body = {};
  if (message) body.message = message;
  const response = await api.post(`${ORGANISATION_URL}/quota-request`, body);
  return response.data;
};

export const resumeLimitMessage = (error) => {
  const data = error?.response?.data || {};
  if (data.code !== "RESUME_LIMIT_REACHED") return "";
  return data.error || ORG_ERROR_COPY.RESUME_LIMIT_REACHED;
};

const ORGANISATION_URL = `${BASE_URL}/api/organisation`;

// Active members, pending and expired invites, and join requests. status and role are optional filters.
export const fetchMembers = async ({ status, role } = {}) => {
  const response = await api.get(`${ORGANISATION_URL}/members`, {
    params: { status: status || undefined, role: role || undefined },
  });
  return {
    members: (response.data?.members || []).map(normalizeMemberRow),
  };
};

export const inviteMember = async ({ email, role, firstName, lastName }) => {
  const body = { email, role };
  if (firstName) body.first_name = firstName;
  if (lastName) body.last_name = lastName;
  const response = await api.post(`${ORGANISATION_URL}/invites`, body);
  return normalizeMemberRow(response.data?.invite || response.data?.member || {});
};

// Resend, revoke, approve, or reject an invite or join request.
export const updateInvite = async (inviteId, { action, role } = {}) => {
  const body = { action };
  if (role) body.role = role;
  const response = await api.patch(`${ORGANISATION_URL}/invites/${inviteId}`, body);
  return response.data || {};
};

export const resendInvite = (inviteId) => updateInvite(inviteId, { action: "resend" });

export const revokeInvite = (inviteId) => updateInvite(inviteId, { action: "revoke" });

export const changeMemberRole = async (userId, role) => {
  const response = await api.patch(`${ORGANISATION_URL}/members/${userId}`, { role });
  return normalizeMemberRow(response.data?.member || {});
};

// Removes a member. Deleting your own user id leaves the organisation.
export const removeMember = async (userId) => {
  const response = await api.delete(`${ORGANISATION_URL}/members/${userId}`);
  return response.data || {};
};

export const approveJoinRequest = (inviteId, role) => updateInvite(inviteId, { action: "approve", role });

export const rejectJoinRequest = (inviteId) => updateInvite(inviteId, { action: "reject" });

export const fetchAuditLog = async ({ page = 1, limit = 25, superAdminOnly = false } = {}) => {
  const response = await api.get(`${CURRENT_URL}/audit-log`, {
    params: { page, limit, super_admin_only: superAdminOnly || undefined },
  });
  return {
    entries: (response.data?.entries || []).map(normalizeAuditEntry),
    count: response.data?.count ?? 0,
  };
};

// Public preview for /invite?token=...
export const fetchInvite = async (token) => {
  const response = await api.get(`${BASE_URL}/api/invites/${encodeURIComponent(token)}`);
  const data = response.data || {};
  return {
    email: data.email || "",
    organisationName: data.organisation_name || "",
    role: data.role ? String(data.role).toLowerCase() : "",
    accountExists: Boolean(data.account_exists),
    status: data.status ? String(data.status).toLowerCase() : "",
    expiresAt: data.expires_at || "",
  };
};

// POST /register/. There is no password: a normal sign-up emails a code for POST /register/verify.
// Invite sign-ups send invite_token. A taken company domain is the same call with request_invitation, and no code is sent.
export const buildRegisterPayload = ({
  email,
  first_name,
  last_name,
  organisation_name,
  invite_token,
  request_invitation,
  message,
} = {}) => {
  const body = {
    email: email ?? "",
    first_name: first_name ?? "",
    last_name: last_name ?? "",
  };
  const organisationName = typeof organisation_name === "string" ? organisation_name.trim() : "";
  if (organisationName) body.organisation_name = organisationName;
  if (invite_token) body.invite_token = invite_token;
  if (request_invitation) body.request_invitation = true;
  const note = typeof message === "string" ? message.trim() : "";
  if (note) body.message = note.slice(0, 1000);
  return body;
};

export const registerAccount = async (fields) => {
  const response = await api.post(`${BASE_URL}/api/register/`, buildRegisterPayload(fields));
  const data = response.data || {};
  return {
    httpStatus: response.status,
    msg: data.msg || "",
    status: data.status || "",
    orgId: data.org_id || "",
    organisationName: data.organisation_name || "",
    accessToken: data.access_token || "",
  };
};

// Step 2 of sign-up. Creates the account and its organisation and returns the same session as login.
export const verifyRegistration = async ({ email, otp }) => {
  const response = await api.post(`${BASE_URL}/api/register/verify`, { email: email.trim(), otp: otp.trim() });
  return response.data || {};
};

// Step 1 of login. 404 USER_NOT_FOUND when there is no account, unless invite_token lets the invite create one.
export const requestLoginCode = async ({ email, inviteToken }) => {
  const body = { email: email.trim() };
  if (inviteToken) body.invite_token = inviteToken;
  const response = await api.post(`${BASE_URL}/api/session/otp`, body);
  return response.data || {};
};

// Step 2 of login. With invite_token the account is created if needed and joins the organisation.
export const verifyLoginCode = async ({ email, otp, inviteToken }) => {
  const body = { email: email.trim(), otp: otp.trim() };
  if (inviteToken) body.invite_token = inviteToken;
  const response = await api.post(`${BASE_URL}/api/session/`, body);
  return response.data || {};
};

// Stores the session returned by login or sign-up. Returns the user, or null when no token came back.
export const storeSession = (data = {}) => {
  if (!data.access_token) return null;
  const { access_token, email, first_name, last_name, user_id } = data;
  const user = { email, first_name, last_name, user_id };
  localStorage.setItem("jwtToken", access_token);
  localStorage.setItem("user_data", JSON.stringify(user));
  applyLoginOrganisation(data);
  return user;
};

// Super Admin console. Search matches name or domain. status is active, suspended, or deleted.
export const fetchAllOrganisations = async ({ search, status, page = 1, limit = 50 } = {}) => {
  const response = await api.get(ADMIN_URL, {
    params: { search: search || undefined, status: status || undefined, page, limit },
  });
  return {
    organisations: (response.data?.organisations || []).map(normalizeOrganisation),
    count: response.data?.total ?? 0,
    page: response.data?.page ?? page,
    limit: response.data?.limit ?? limit,
  };
};

// No domain, so the organisation never matches a company sign-up. resume_limit defaults to 100.
export const createOrganisation = async ({ name, ownerEmail, ownerFirstName = "", ownerLastName = "", resumeLimit }) => {
  const body = {
    name,
    owner_email: ownerEmail,
    owner_first_name: ownerFirstName,
  };
  if (ownerLastName) body.owner_last_name = ownerLastName;
  if (resumeLimit !== undefined && resumeLimit !== "") body.resume_limit = resumeLimit;
  const response = await api.post(ADMIN_URL, body);
  return {
    organisation: normalizeOrganisation(response.data?.organisation || {}),
    ownerInvited: Boolean(response.data?.owner_invited),
  };
};

export const updateOrganisationAsAdmin = async (organisationId, { name, status, resumeLimit, ownerUserId, ownerEmail } = {}) => {
  const body = {};
  if (name !== undefined) body.name = name;
  if (status !== undefined) body.status = status;
  if (resumeLimit !== undefined) body.resume_limit = resumeLimit;
  if (ownerUserId !== undefined) body.owner_user_id = ownerUserId;
  if (ownerEmail !== undefined) body.owner_email = ownerEmail;
  const response = await api.patch(`${ADMIN_URL}/${organisationId}`, body);
  return normalizeOrganisation(response.data?.organisation || {});
};

export const deleteOrganisationAsAdmin = (organisationId) =>
  updateOrganisationAsAdmin(organisationId, { status: ORG_STATUS.DELETED });

// A Super Admin session outside any organisation (spec 1.2: a flag, not a membership)
export const isSuperAdminMode = (session) => Boolean(session.isSuperAdmin && !session.role);

// Leaves a support session and returns to the Super Admin's own session
export const exitOrganisation = async () => {
  const response = await api.post(`${ADMIN_URL}/exit`);
  return response.data?.access_token;
};

// Hides the end of the name part so the address is recognisable but not fully shown:
// tmanthiriyappan+support@fleetstudio.com -> tmanthiriyappan+sup*****@fleetstudio.com
export const maskEmail = (email) => {
  const [name, domain] = String(email || "").trim().split("@");
  if (!name || !domain) return "";
  return `${name.slice(0, Math.max(1, name.length - 4))}*****@${domain}`;
};

export const orgExistsMessage = (organisationName) =>
  `${organisationName || "This company"} already uses SiftHire`;

export const ORG_ERROR_COPY = {
  EMAIL_REQUIRED: "Email is required.",
  EMAIL_INVALID: "Enter a valid email.",
  FREE_EMAIL_NOT_ALLOWED: "Please sign up with your company email address.",
  FREE_EMAIL_DOMAIN: "Please sign up with your company email address.",
  USER_EXISTS: "You already have an account. Sign in to continue.",
  ORGANISATION_EXISTS: "This company already uses SiftHire.",
  OWNER_MUST_TRANSFER: "Transfer ownership to another member before leaving.",
  OWNER_ROLE_LOCKED: "Transfer ownership before changing the Owner's role.",
  OWNER_CONFLICT: "Ownership just changed. Try again.",
  CANNOT_REMOVE_OWNER: "Transfer ownership before removing the Owner.",
  MEMBER_NOT_FOUND: "That person is no longer on this team.",
  ROLE_NOT_ALLOWED: "You don't have permission to give that role.",
  ALREADY_MEMBER: "This person is already a member of the organisation.",
  INVITE_EXISTS: "This person already has an invite. Resend that one instead.",
  JOIN_REQUEST_EXISTS: "This person already asked to join. Approve their request instead.",
  INVITE_NOT_FOUND: "That invite could not be found.",
  INVALID_INVITE_STATE: "This invite was already updated. Refresh and try again.",
  VALIDATION_ERROR: "Check the details and try again.",
  INVITE_EXPIRED: "This invite has expired. Ask the person who invited you to resend it.",
  INVITE_INVALID: "This invite is unknown, already used, or was revoked.",
  INVITE_EMAIL_MISMATCH: "Use the email address this invite was sent to.",
  RESUME_LIMIT_REACHED: "Resume limit reached. The organisation Owner can request more.",
  EMAIL_FAILED: "The request could not be emailed. Try again.",
  ORGANISATION_SUSPENDED: "This organisation is suspended. Contact support.",
  ORG_NOT_FOUND: "That organisation could not be found.",
  USER_NOT_FOUND: "That user could not be found. The new Owner must already have an account.",
  NOT_A_MEMBER: "You are not a member of that organisation.",
  ORG_INACTIVE: "That organisation is inactive.",
  FORBIDDEN: "You don't have permission to do that.",
  EMAIL_NOT_EDITABLE: "Your email can't be changed.",
  NOT_A_PANELIST: "You don't have a panelist profile in this organisation. Reload the page and try again.",
  PANELIST_NOT_FOUND: "Your panelist profile was removed while saving, so nothing was saved. Reload the page.",
};

const PROFILE_FIELD = {
  first_name: "firstName",
  last_name: "lastName",
  designation: "panelist",
  skills: "panelist",
  experience: "panelist",
  experience_plus: "panelist",
};

// Maps a failed PATCH /api/me onto the form field it belongs to. field is "" for a form-level error.
export const interpretProfileError = (error) => {
  const data = error?.response?.data || {};
  const field = data.code === "VALIDATION_ERROR" ? PROFILE_FIELD[data.details?.field] || "" : "";
  return {
    field,
    message: ORG_ERROR_COPY[data.code] && data.code !== "VALIDATION_ERROR"
      ? ORG_ERROR_COPY[data.code]
      : data.error || data.message || "Could not save your profile. Try again.",
  };
};

// Joining uses a seat. When the plan has none left (or the plan has ended) the invite can't be accepted, and the
// person should be told who can fix it rather than see a bare failure.
export const INVITE_PLAN_COPY = {
  PLAN_LIMIT_REACHED: "This organisation has no seats left on its plan, so you can't join yet. Ask the organisation admin to upgrade or add seats, then use this invite again.",
  NO_ACTIVE_PLAN: "This organisation doesn't have an active plan, so you can't join yet. Ask the organisation admin to buy a plan.",
};

// Login succeeded, but the invite_token on the request could not be used.
export const inviteErrorMessage = (inviteError) => {
  if (!inviteError) return "";
  return INVITE_PLAN_COPY[inviteError.code] || ORG_ERROR_COPY[inviteError.code] || inviteError.error || "This invite couldn't be used.";
};

export const orgErrorMessage = (error, fallback) => {
  const data = error?.response?.data || {};
  return ORG_ERROR_COPY[data.code] || data.error || data.message || data.msg || fallback;
};

// Maps a failed POST /register/ onto a field, the login page, or the request-an-invitation step.
export const interpretRegisterError = (error) => {
  const data = error?.response?.data || {};
  const code = data.code || "";
  const details = data.details || {};
  if (code === "EMAIL_REQUIRED" || code === "EMAIL_INVALID") {
    return { kind: "field", field: "email", message: ORG_ERROR_COPY[code] };
  }
  if (code === "FREE_EMAIL_NOT_ALLOWED" || code === "FREE_EMAIL_DOMAIN") {
    return { kind: "field", field: "email", message: ORG_ERROR_COPY.FREE_EMAIL_NOT_ALLOWED };
  }
  if (code === "USER_EXISTS") {
    return { kind: "login", message: ORG_ERROR_COPY.USER_EXISTS };
  }
  if (code === "ORG_EXISTS_FOR_DOMAIN" || code === "ORGANISATION_EXISTS") {
    const organisationName = details.organisation_name || "";
    return {
      kind: "org_exists",
      organisationName,
      domain: details.domain || "",
      ownerEmail: details.owner_email || "",
      message: orgExistsMessage(organisationName),
    };
  }
  if (code === "INVITE_INVALID" || code === "INVITE_EXPIRED" || code === "INVITE_EMAIL_MISMATCH") {
    return { kind: "invite", code, message: ORG_ERROR_COPY[code] };
  }
  return { kind: "generic", message: data.error || data.message || data.msg || ORG_ERROR_COPY[code] || "" };
};
